import { useLayoutEffect, useRef, useState } from 'react';

// Where a floating popup (dropdown list, calendar) opens: below its field, or
// above it when there is more room above — and drawn over everything, so
// nothing can cut it off.
//
// It used to sit inside the field's own box (position: absolute), so any
// box around it that clips its contents — a collapsible section, a card
// that scrolls, a modal — cut the list short: two options showing out of
// twenty. Now the popup is pinned to the window (position: fixed) at the
// field's place, kept clear of the fixed bottom tab bar, and given as much
// height as there is room for in the direction it opens; a list inside it
// that carries data-popup-list takes the room left over (the
// --popup-list-room variable, used by POPUP_LIST_MAX) and scrolls beyond
// that. It follows the field if the page scrolls or resizes.
//
// Usage: put anchorRef on the wrapper around the field, popupRef on the
// popup, and popupClass(dropUp) on the popup instead of its own
// absolute/mt-1/z-index classes. matchWidth: false for a popup with its own
// width (the calendar).
const BOTTOM_BAR_HEIGHT = 64; // FSM's tab bar; harmless where there is none
const GAP = 4;
const EDGE = 8;

export default function usePopupPlacement(open, { enabled = true, matchWidth = true } = {}) {
  const anchorRef = useRef(null);
  const popupRef = useRef(null);
  const [dropUp, setDropUp] = useState(false);

  useLayoutEffect(() => {
    if (!open || !enabled || !anchorRef.current || !popupRef.current) return undefined;
    const place = () => {
      const anchor = anchorRef.current;
      const popup = popupRef.current;
      if (!anchor || !popup) return;
      popup.style.position = 'fixed'; // before measuring, so it never sits in the field's box
      const rect = anchor.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - BOTTOM_BAR_HEIGHT - GAP - EDGE;
      const above = rect.top - GAP - EDGE;
      // its natural height, before any limit
      popup.style.maxHeight = '';
      popup.style.setProperty('--popup-list-room', '9999px');
      const natural = popup.offsetHeight;
      const up = natural > below && above > below;
      const room = Math.max(120, up ? above : below);
      const list = popup.querySelector('[data-popup-list]');
      const extra = list ? popup.offsetHeight - list.offsetHeight : 0;
      const width = matchWidth ? rect.width : popup.offsetWidth;
      const left = Math.min(Math.max(EDGE, rect.left), window.innerWidth - width - EDGE);
      Object.assign(popup.style, {
        position: 'fixed',
        left: `${left}px`,
        width: matchWidth ? `${rect.width}px` : '',
        top: up ? 'auto' : `${rect.bottom + GAP}px`,
        bottom: up ? `${window.innerHeight - rect.top + GAP}px` : 'auto',
        margin: '0',
        maxHeight: `${room}px`,
        overflowY: list ? '' : 'auto',
      });
      popup.style.setProperty('--popup-list-room', `${Math.max(80, room - extra)}px`);
      setDropUp(up);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true); // any scrolling box, not just the page
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, enabled, matchWidth]);

  return { anchorRef, popupRef, dropUp };
}

// z-50: above the fixed bottom tab bar (z-20) and the page's own layers. The
// coordinates are set by the hook.
export function popupClass() {
  return 'fixed z-50'; // out of the page's flow from the first frame, so it never stretches the field
}

// The max-height for a scrolling list inside a popup (with data-popup-list):
// as much as there is room for, up to about ten rows.
export const POPUP_LIST_MAX = 'max-h-[min(22rem,var(--popup-list-room,15rem))]';
